import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  Button,
  Alert,
  Table,
  Switch,
  Input,
  Modal,
  Tag,
  Row,
  Col,
  Space,
} from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useTender } from "../../contexts/TenderContext";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import MainLayout from "../layout/MainLayout";
import { BoQGenerator } from "../../utils/boqGenerator";
import { BoQExporter } from "../../utils/boqExports";
import {
  BoQSection,
  BoQSummary,
  BoQExportOptions,
} from "../../types/tender.types";

const { Title, Text } = Typography;

const BillOfQuantities: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state } = useTender();
  const [boqSections, setBoqSections] = useState<BoQSection[]>([]);
  const [boqSummary, setBoqSummary] = useState<BoQSummary | null>(null);
  const [exportOptions, setExportOptions] = useState<BoQExportOptions>({
    includeBreakdown: true,
    includeNotes: false,
    showRatesTo: 2,
    groupByCategory: true,
    companyHeader: true,
  });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");
  const [marginPercentage, setMarginPercentage] = useState(0);

  const calculationResults = state.calculationResults;
  const hasCalculations =
    calculationResults &&
    calculationResults.isCalculated &&
    calculationResults.validationErrors.length === 0;

  // Generate BoQ when calculation results are available
  useEffect(() => {
    if (hasCalculations) {
      const { sections, summary } = BoQGenerator.generateBoQ(
        calculationResults,
        state
      );
      setBoqSections(sections);
      setBoqSummary(summary);
    }
  }, [calculationResults, hasCalculations, state]);

  const handleExportToExcel = async () => {
    if (!boqSections.length || !boqSummary) return;

    try {
      // Try new ExcelJS export with logo first
      await BoQExporter.exportToExcelWithLogo(
        boqSections,
        boqSummary,
        state,
        exportOptions
      );
    } catch (error) {
      console.warn("ExcelJS export failed, using fallback:", error);
      // Fallback to original XLSX export
      BoQExporter.exportToExcel(boqSections, boqSummary, state, exportOptions);
    }
  };

  const handleExportToPDF = () => {
    if (!boqSections.length || !boqSummary) return;

    BoQExporter.exportToPDF(boqSections, boqSummary, state, exportOptions);
  };

  const handlePrintPreview = () => {
    if (!boqSections.length || !boqSummary) return;

    const html = BoQExporter.generatePrintHTML(boqSections, boqSummary, state);
    setPreviewHTML(html);
    setPreviewDialogOpen(true);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(previewHTML);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const formatCurrency = (amount: number) =>
    `£${amount.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const applyMargin = (amount: number) =>
    marginPercentage > 0 ? amount * (1 + marginPercentage / 100) : amount;

  if (!hasCalculations) {
    return (
      <MainLayout canProceed={false}>
        <div className="max-w-[1400px] mx-auto">
          <Title level={2} className="mb-6 text-[#003366] font-semibold">
            Bill of Quantities
          </Title>
          <Alert
            message="Please complete the calculation process in the BOQ section before generating the Bill of Quantities."
            type="warning"
            showIcon
          />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout canProceed={true}>
      <div className="max-w-[1400px] mx-auto">
        <Title level={2} className="mb-6 text-[#003366] font-semibold">
          Bill of Quantities
        </Title>

        {/* Project Header */}
        <Card className="mb-6 shadow-md">
          <Title
            level={3}
            className="text-center font-bold text-[#003366] mb-4"
          >
            M&J EVANS CONSTRUCTION LIMITED
          </Title>
          <Title
            level={4}
            className="text-center font-semibold text-[#003366] mb-6"
          >
            BILL OF QUANTITIES
          </Title>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text>
                <strong>Project:</strong> {state.projectName}
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Text>
                <strong>Client:</strong> {state.client}
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Text>
                <strong>Date:</strong> {new Date().toLocaleDateString()}
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Export Options */}
        <Card className="mb-6 shadow-md">
          <Title level={4} className="mb-4 text-[#003366] font-semibold">
            Export Options
          </Title>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Switch
                  checked={exportOptions.companyHeader}
                  onChange={(checked) =>
                    setExportOptions((prev) => ({
                      ...prev,
                      companyHeader: checked,
                    }))
                  }
                />
                <span>Company Header</span>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <span>Margin %:</span>
                <Input
                  type="number"
                  value={marginPercentage}
                  onChange={(e) =>
                    setMarginPercentage(parseFloat(e.target.value) || 0)
                  }
                  min={0}
                  max={50}
                  step={0.5}
                  className="w-32"
                />
              </Space>
            </Col>
          </Row>

          <div className="mt-4 flex gap-2 flex-wrap">
            <Button
              type="primary"
              onClick={handleExportToExcel}
              icon={<DownloadOutlined />}
              className="bg-[#FF6B35] hover:bg-[#e55a2b] border-[#FF6B35]"
            >
              Export to Excel
            </Button>

            <Button
              onClick={handlePrintPreview}
              icon={<EyeOutlined />}
              className="border-[#003366] text-[#003366] hover:border-[#002244] hover:text-[#002244]"
            >
              Print Preview
            </Button>
          </div>
        </Card>

        {/* BoQ Table - 2 Column Format */}
        <Card className="mb-6 shadow-md">
          <div className="max-h-96 overflow-y-auto border border-gray-200">
            <Table
              size="small"
              pagination={false}
              showHeader={true}
              bordered
              columns={[
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                  width: "70%",
                  className: "text-center font-semibold",
                  render: (text: string, record: any) => (
                    <div
                      className={`${
                        record.fontWeight === "bold"
                          ? "font-bold"
                          : "font-normal"
                      } text-${record.fontSize || "sm"}`}
                    >
                      {text}
                      {record.manuallyAdjusted && (
                        <Tag color="orange" className="ml-1">
                          M
                        </Tag>
                      )}
                      {record.notes && (
                        <div className="text-xs text-gray-500 mt-1">
                          {record.notes}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  width: "30%",
                  align: "right" as const,
                  className: "text-center font-semibold",
                  render: (amount: number, record: any) => (
                    <div
                      className={`${
                        record.fontWeight === "bold"
                          ? "font-bold"
                          : "font-normal"
                      } text-${record.fontSize || "sm"}`}
                    >
                      {amount > 0 && formatCurrency(applyMargin(amount))}
                      {record.status && (
                        <div className="text-xs text-gray-500 italic mt-1">
                          {record.status}
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
              dataSource={[
                ...boqSections.flatMap((section) =>
                  section.items.map((item, index) => {
                    let rowClassName = "";
                    let fontWeight = "normal";
                    let fontSize = "sm";

                    // Header styling
                    if (item.isHeader && item.level === 0) {
                      rowClassName = "bg-blue-50";
                      fontWeight = "bold";
                      fontSize = "base";
                    }

                    // Yellow highlighting for savings items and capping layers
                    if (item.isHighlighted || item.isSavingsItem) {
                      rowClassName = "bg-yellow-300";
                    }

                    // Empty row styling
                    if (item.description === "") {
                      rowClassName = "h-2";
                    }

                    // Review flagging
                    if (item.flaggedForReview && !item.isHighlighted) {
                      rowClassName = "bg-yellow-50";
                    }

                    return {
                      key: `${section.id}-${index}`,
                      description: item.description,
                      amount: item.amount,
                      fontWeight,
                      fontSize,
                      rowClassName,
                      manuallyAdjusted: item.manuallyAdjusted,
                      notes: item.notes,
                      status: item.status,
                    };
                  })
                ),
                // Grand Total Row
                {
                  key: "grand-total",
                  description: "GRAND TOTAL",
                  amount: boqSummary?.grandTotal || 0,
                  fontWeight: "bold",
                  fontSize: "lg",
                  rowClassName: "bg-green-100",
                },
              ]}
              rowClassName={(record) => record.rowClassName || ""}
            />
          </div>
        </Card>

        {/* Summary Section */}
        {boqSummary && (
          <Card className="mb-6 shadow-md">
            <div className="p-6">
              <Typography.Title
                level={4}
                className="mb-4 text-[#003366] font-semibold"
              >
                Summary
              </Typography.Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Typography.Title level={5} className="mb-2 font-semibold">
                    Cost Breakdown
                  </Typography.Title>
                  <Table
                    size="small"
                    showHeader={false}
                    pagination={false}
                    bordered
                    columns={[
                      { dataIndex: "label", key: "label" },
                      {
                        dataIndex: "value",
                        key: "value",
                        align: "right" as const,
                      },
                    ]}
                    dataSource={[
                      {
                        key: "1",
                        label: "Labour Costs",
                        value: formatCurrency(
                          applyMargin(boqSummary.labourTotal)
                        ),
                      },
                      {
                        key: "2",
                        label: "Material Costs",
                        value: formatCurrency(
                          applyMargin(boqSummary.materialTotal)
                        ),
                      },
                      {
                        key: "3",
                        label: "Plant Costs",
                        value: formatCurrency(
                          applyMargin(boqSummary.plantTotal)
                        ),
                      },
                      {
                        key: "4",
                        label: "Construction Subtotal",
                        value: formatCurrency(
                          applyMargin(boqSummary.constructionSubtotal)
                        ),
                        className: "bg-gray-100 font-semibold",
                      },
                      {
                        key: "5",
                        label: "Overhead (15%)",
                        value: formatCurrency(applyMargin(boqSummary.overhead)),
                      },
                      {
                        key: "6",
                        label: "Profit (8%)",
                        value: formatCurrency(applyMargin(boqSummary.profit)),
                      },
                      {
                        key: "7",
                        label: "Grand Total",
                        value: formatCurrency(
                          applyMargin(boqSummary.grandTotal)
                        ),
                        className: "bg-green-100 font-bold text-lg",
                      },
                    ]}
                    rowClassName={(record) => record.className || ""}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <Typography.Title level={5} className="mb-2 font-semibold">
                    Section Totals
                  </Typography.Title>
                  <Table
                    size="small"
                    showHeader={false}
                    pagination={false}
                    bordered
                    columns={[
                      { dataIndex: "label", key: "label" },
                      {
                        dataIndex: "value",
                        key: "value",
                        align: "right" as const,
                      },
                    ]}
                    dataSource={[
                      {
                        key: "1",
                        label: "Foul Sewers",
                        value: formatCurrency(
                          applyMargin(boqSummary.sections.foulSewers)
                        ),
                      },
                      {
                        key: "2",
                        label: "Storm Sewers",
                        value: formatCurrency(
                          applyMargin(boqSummary.sections.stormSewers)
                        ),
                      },
                      {
                        key: "3",
                        label: "Roads",
                        value: formatCurrency(
                          applyMargin(boqSummary.sections.roads)
                        ),
                      },
                      {
                        key: "4",
                        label: "Preliminary Items",
                        value: formatCurrency(
                          applyMargin(boqSummary.sections.preliminaryItems)
                        ),
                      },
                    ]}
                  />
                </Col>
              </Row>

              {marginPercentage > 0 && (
                <Alert
                  message={`A ${marginPercentage}% margin has been applied to all amounts shown above.`}
                  type="info"
                  className="mt-4"
                />
              )}
            </div>
          </Card>
        )}

        {/* Print Preview Dialog */}
        <Modal
          open={previewDialogOpen}
          onCancel={() => setPreviewDialogOpen(false)}
          width={800}
          title="Print Preview"
          footer={[
            <Button key="close" onClick={() => setPreviewDialogOpen(false)}>
              Close
            </Button>,
            <Button key="print" type="primary" onClick={handlePrint}>
              Print
            </Button>,
          ]}
        >
          <div
            className="border border-gray-300 min-h-96 bg-white p-4"
            dangerouslySetInnerHTML={{ __html: previewHTML }}
          />
        </Modal>
      </div>
    </MainLayout>
  );
};

export default BillOfQuantities;
